import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Gauge, Ruler, Anchor, Users, Hash, Calendar, MapPin, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ImageSlot } from "@/components/ImageSlot";
import { LeadDialog } from "@/components/public/LeadDialog";
import { Seo } from "@/components/Seo";
import { api, fileUrl, formatPrice, STATUS_LABELS, STATUS_COLORS } from "@/lib/api";

const splitList = (s) => {
  if (!s) return [];
  const parts = s.includes("\n") ? s.split("\n") : s.split(",");
  return parts.map((x) => x.replace(/^[•\-*\u2022]\s*/, "").trim()).filter(Boolean);
};

export default function BoatDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [boat, setBoat] = useState(null);
  const [active, setActive] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setBoat(null); setNotFound(false); setActive(0);
    api.get(`/boats/${slug}`).then((r) => setBoat(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-4 py-24 text-center">
      <h1 className="text-3xl font-bold font-heading text-slate-900">Boat not found</h1>
      <Button asChild className="mt-6 bg-mtm-purple text-white rounded-full"><Link to="/inventory">Browse Inventory</Link></Button>
    </div>
  );
  if (!boat) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-slate-500">Loading...</div>;

  const photos = (boat.photos || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const isSold = boat.status === "sold";
  const salePrice = boat.promo_price || boat.price;
  const hasPromo = boat.promo_price && boat.price && boat.promo_price < boat.price;
  const specs = [
    ["Length", boat.length], ["Beam", boat.beam], ["Dry Weight", boat.dry_weight],
    ["Fuel Capacity", boat.fuel_capacity], ["Live Well", boat.live_well_capacity], ["Draft", boat.draft],
    ["Max HP", boat.max_hp], ["Length on Trailer", boat.length_on_trailer],
    ["Passengers", boat.passenger_capacity], ["Engine", `${boat.engine_make || ""} ${boat.engine_model || ""}`.trim()],
    ["Horsepower", boat.horsepower && `${boat.horsepower} HP`], ["Engine Hours", boat.engine_hours],
    ["HIN", boat.hin], ["Location", boat.location],
  ].filter(([, v]) => v);

  const relatedLink = boat.brand === "Falcon" ? "/brands/falcon"
    : boat.brand === "Armada" ? "/brands/armada"
    : boat.condition === "used" ? "/inventory?condition=used" : "/inventory";
  const relatedLabel = boat.brand === "Falcon" ? "View Current Falcon Inventory"
    : boat.brand === "Armada" ? "View Current Armada Inventory"
    : boat.condition === "used" ? "View Current Used Inventory" : "View Current Inventory";

  return (
    <div data-testid="boat-detail-page">
      <Seo
        title={boat.seo_title || `${boat.condition === "new" ? "New" : "Used"} ${boat.year} ${boat.brand} ${boat.model} | MTM Marine`}
        description={boat.meta_description || `${boat.year} ${boat.brand} ${boat.model} for sale at MTM Marine in Bristol, TN. ${boat.description || ""}`.slice(0, 160)}
        noindex={boat.noindex}
        image={photos[0] ? fileUrl(photos[0].storage_path, 1200) : undefined}
        jsonLd={{
          "@context": "https://schema.org", "@type": "Product",
          name: `${boat.year} ${boat.brand} ${boat.model}`, description: boat.description,
          sku: boat.stock_number,
          offers: salePrice ? { "@type": "Offer", price: salePrice, priceCurrency: "USD",
            availability: isSold ? "https://schema.org/SoldOut" : "https://schema.org/InStock" } : undefined,
        }}
      />

      <div className="max-w-7xl mx-auto px-4 pt-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-mtm-purple" data-testid="back-button"><ArrowLeft size={15} /> Back</button>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Gallery + info */}
        <div className="lg:col-span-2">
          <div className="relative">
            {photos.length ? (
              <div className="aspect-[16/10] w-full rounded-xl overflow-hidden bg-slate-100">
                <img src={fileUrl(photos[active].storage_path, 1400)} alt={photos[active].alt_text || `${boat.year} ${boat.brand} ${boat.model}`} className="w-full h-full object-cover" />
              </div>
            ) : (
              <ImageSlot label={`${boat.year}-${boat.brand}-${boat.model}-${boat.stock_number}-001.jpg`} dimensions="1400 × 875" aspect="aspect-[16/10]" />
            )}
            <div className="absolute top-4 left-4 flex gap-2">
              <span className={`text-xs font-bold uppercase px-3 py-1 rounded-full ${boat.condition === "new" ? "bg-mtm-purple text-white" : "bg-slate-800 text-white"}`}>{boat.condition === "new" ? "New" : "Used"}</span>
              <span className={`text-xs font-bold uppercase px-3 py-1 rounded-full ${STATUS_COLORS[boat.status]}`}>{STATUS_LABELS[boat.status]}</span>
            </div>
            {isSold && <div className="absolute inset-0 bg-black/40 rounded-xl flex items-center justify-center"><span className="bg-red-600 text-white font-heading font-extrabold text-3xl px-8 py-3 rounded-lg -rotate-6 shadow-xl" data-testid="sold-badge-detail">SOLD</span></div>}
          </div>
          {photos.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-3">
              {photos.map((p, i) => (
                <button key={p.id} onClick={() => setActive(i)} className={`aspect-square rounded-lg overflow-hidden border-2 ${i === active ? "border-mtm-purple" : "border-transparent"}`} data-testid={`thumb-${i}`}>
                  <img src={fileUrl(p.storage_path, 300)} alt={p.alt_text || ""} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-8">
            <h1 className="text-3xl sm:text-4xl font-extrabold font-heading text-slate-900">{boat.year} {boat.brand} {boat.model}</h1>
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-500 font-mono">
              {boat.stock_number && <span className="flex items-center gap-1"><Hash size={14} /> Stock #{boat.stock_number}</span>}
              {boat.boat_type && <span className="flex items-center gap-1"><Anchor size={14} /> {boat.boat_type}</span>}
              {boat.location && <span className="flex items-center gap-1"><MapPin size={14} /> {boat.location}</span>}
            </div>

            {boat.description && <p className="text-slate-600 mt-5 leading-relaxed">{boat.description}</p>}

            {specs.length > 0 && (
              <div className="mt-8">
                <h2 className="font-heading font-bold text-xl text-slate-900 mb-4">Specifications</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-slate-200 rounded-xl overflow-hidden border border-slate-200">
                  {specs.map(([k, v]) => (
                    <div key={k} className="bg-white p-4">
                      <div className="text-[11px] uppercase tracking-wide text-slate-400 font-mono">{k}</div>
                      <div className="text-sm font-semibold text-slate-900 mt-0.5">{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {boat.standard_features && splitList(boat.standard_features).length > 0 && (
              <div className="mt-8">
                <h2 className="font-heading font-bold text-xl text-slate-900 mb-4">Standard Features</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="boat-standard-features">
                  {splitList(boat.standard_features).map((o, i) => <div key={i} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 size={16} className="text-mtm-teal" /> {o}</div>)}
                </div>
              </div>
            )}
            {boat.standard_specs && splitList(boat.standard_specs).length > 0 && (
              <div className="mt-8">
                <h2 className="font-heading font-bold text-xl text-slate-900 mb-4">Trailer Specs</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="boat-standard-specs">
                  {splitList(boat.standard_specs).map((o, i) => <div key={i} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 size={16} className="text-mtm-teal" /> {o}</div>)}
                </div>
              </div>
            )}
            {(boat.about_boat || boat.about_model) && (
              <div className="mt-8" data-testid="boat-about-section">
                <h2 className="font-heading font-bold text-xl text-slate-900 mb-4">About the {boat.year} {boat.brand} {boat.model}</h2>
                <div className="prose prose-slate max-w-none text-slate-700 space-y-4 leading-relaxed">
                  {(boat.about_boat || boat.about_model).split(/\n\s*\n/).map((p, i) => <p key={i} className="text-base">{p.trim()}</p>)}
                </div>
              </div>
            )}
            {(boat.options || []).length > 0 && (
              <div className="mt-8">
                <h2 className="font-heading font-bold text-xl text-slate-900 mb-4">Options & Features</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {boat.options.map((o, i) => <div key={i} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 size={16} className="text-mtm-teal" /> {o}</div>)}
                </div>
              </div>
            )}
            {boat.trailer_info && <p className="mt-6 text-sm text-slate-600"><strong>Trailer:</strong> {boat.trailer_info}</p>}
            {boat.estimated_arrival && (boat.status === "en_route" || boat.status === "coming_soon") && (
              <p className="mt-2 text-sm text-mtm-teal-dark font-semibold flex items-center gap-1"><Calendar size={15} /> Estimated arrival: {boat.estimated_arrival}</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <aside>
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm sticky top-24">
            {isSold ? (
              <div data-testid="sold-sidebar">
                <div className="flex items-center gap-2 text-red-600 font-semibold"><AlertCircle size={18} /> This boat has been sold.</div>
                <p className="text-sm text-slate-600 mt-2">Thank you for your interest. Browse our current inventory for similar boats.</p>
                <Button asChild className="w-full mt-4 bg-mtm-purple hover:bg-mtm-purple-deep text-white rounded-full" data-testid="sold-related-cta"><Link to={relatedLink}>{relatedLabel}</Link></Button>
              </div>
            ) : (
              <>
                {boat.price_hidden || salePrice == null ? (
                  <div className="text-xl font-bold text-mtm-purple">Call for Price</div>
                ) : (
                  <div>
                    {hasPromo && <div className="text-sm text-slate-400 line-through">{formatPrice(boat.price)}</div>}
                    <div className="font-heading font-extrabold text-3xl text-mtm-purple-deep">{formatPrice(salePrice)}</div>
                    {hasPromo && <span className="inline-block mt-1 text-xs font-bold text-mtm-teal-dark bg-mtm-teal/10 px-2 py-0.5 rounded-full">Save {formatPrice(boat.price - boat.promo_price)}</span>}
                  </div>
                )}
                <div className="space-y-2.5 mt-5">
                  <LeadDialog ctaType="Request Information" boat={boat} variant="purple" className="w-full" testId="inquire-request-info" />
                  <LeadDialog ctaType="Check Availability" boat={boat} variant="teal" className="w-full" testId="inquire-check-availability" />
                  <LeadDialog ctaType="Schedule a Showing" boat={boat} variant="outline" className="w-full" testId="inquire-schedule-showing" />
                  <LeadDialog ctaType="Financing Information" boat={boat} variant="outline" className="w-full" testId="inquire-financing" />
                </div>
                <p className="text-xs text-slate-400 mt-4 text-center">Or call us at <a href="tel:4238783520" className="text-mtm-purple font-semibold">423-878-3520</a></p>
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
